// pages/CompetencyQuiz/AssignTab.js
//
// "only IT can assign it to someone" — IT Admin / IT Team (competencyQuiz.manage)
// assign or unassign the quiz per person. Assigning is a DIRECT grant of
// "Competency Quiz - Open" made through the same write path as Access Management
// (delegation guard, live-cache refresh, audit), so the two screens always agree.
// People who get the quiz from their ROLE are not listed as assigned here; change
// those in Access Management > Role Permissions.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, ButtonGroup, Col, Input, Row, Spinner, Table } from "reactstrap";
import { toast } from "react-toastify";
import useAuth from "../Personal Attendance/useAuth";
import { EmptyState, useDebouncedValue } from "../../Components/Common/DashboardKit";
import { assign, errorText, listAssignments, pick, unassign } from "../../services/competencyQuizApi";
import { BandBadge, fmtDateTime, fmtPercent } from "./quizFormat";
import useSingleFlight from "./useSingleFlight";

const SHOW_LIMIT = 200;

export default function AssignTab() {
    const navigate = useNavigate();
    const { empId: myEmpId, isItAdmin, canAccessModule } = useAuth();
    const canOpenResults = isItAdmin || canAccessModule("competencyQuiz.results");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);          // empId being changed
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("assigned"); // assigned | not | all
    const debounced = useDebouncedValue(search, 250);
    const once = useSingleFlight();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listAssignments();
            setRows((data || []).map((r) => ({
                empId:      pick(r, "EmpId", "empId"),
                name:       pick(r, "Name", "name") || "",
                department: pick(r, "Department", "department") || "",
                role:       pick(r, "Role", "role") || "",
                active:     (pick(r, "EmpStatus", "empStatus") || "").toUpperCase() === "A",
                isItAdmin:  !!pick(r, "IsItAdmin", "isItAdmin"),
                assigned:   !!pick(r, "Assigned", "assigned"),
                last:       pick(r, "LastAttempt", "lastAttempt") || null,
            })));
        } catch (err) {
            toast.error(errorText(err, "Could not load employees."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // A search always looks through EVERYONE (the Assigned / Not assigned buttons only
    // apply while the search box is empty), so typing a name finds the person to assign.
    const searching = debounced.trim().length > 0;
    const visible = useMemo(() => {
        const s = debounced.trim().toLowerCase();
        if (s) {
            return rows.filter((r) => r.name.toLowerCase().includes(s) || String(r.empId).toLowerCase().includes(s)
                || r.department.toLowerCase().includes(s) || r.role.toLowerCase().includes(s));
        }
        return rows.filter((r) => (filter === "assigned" ? r.assigned : filter === "not" ? !r.assigned && r.active : true));
    }, [rows, filter, debounced]);

    const toggle = (r) => once(`assign:${r.empId}`, async () => {
        setBusy(r.empId);
        try {
            const res = r.assigned ? await unassign(r.empId) : await assign(r.empId);
            setRows((list) => list.map((x) => (x.empId === r.empId ? { ...x, assigned: !r.assigned } : x)));
            const warning = pick(res, "Warning", "warning");
            if (warning) toast.warning(warning, { autoClose: 9000 });
            else if (r.assigned) toast.success(`${r.name || r.empId} no longer has the quiz.`);
            else toast.success(`Quiz assigned to ${r.name || r.empId}. It appears in their menu after they sign in again (or within 30 minutes).`, { autoClose: 7000 });
        } catch (err) {
            toast.error(errorText(err, "The change was not saved."));
        } finally {
            setBusy(null);
        }
    });

    const assignedCount = rows.filter((r) => r.assigned).length;

    return (
        <>
            <Row className="g-2 align-items-center mb-3">
                <Col md={5}>
                    <Input placeholder="Search name, ID, department or role" value={search} onChange={(e) => setSearch(e.target.value)} />
                </Col>
                <Col md={4}>
                    <ButtonGroup size="sm">
                        <Button color={!searching && filter === "assigned" ? "primary" : "light"} onClick={() => { setSearch(""); setFilter("assigned"); }}>Assigned ({assignedCount})</Button>
                        <Button color={!searching && filter === "not" ? "primary" : "light"} onClick={() => { setSearch(""); setFilter("not"); }}>Not assigned</Button>
                        <Button color={!searching && filter === "all" ? "primary" : "light"} onClick={() => { setSearch(""); setFilter("all"); }}>All</Button>
                    </ButtonGroup>
                    {searching && <span className="small text-muted ms-2">Searching everyone</span>}
                </Col>
                <Col md={3} className="text-md-end small text-muted">
                    Finishing the quiz unassigns the person automatically; assign again for a retake.
                    IT Admins always have the quiz; role-based access is in Access Management.
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-5"><Spinner size="sm" /> Loading…</div>
            ) : visible.length === 0 ? (
                <EmptyState
                    title={searching ? "No one matches the search" : filter === "assigned" ? "Nobody is assigned yet" : "Nobody to show"}
                    message={searching ? "Check the spelling, or search by employee ID." : filter === "assigned" ? "Type a name above, or choose \"Not assigned\", to find people and assign them the quiz." : undefined}
                />
            ) : (
                <>
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Employee</th>
                                    <th>Dept</th>
                                    <th>Role</th>
                                    <th>Latest attempt</th>
                                    <th className="text-end">Access</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.slice(0, SHOW_LIMIT).map((r) => {
                                    const self = String(r.empId).trim() === String(myEmpId).trim();
                                    const lastId = r.last && pick(r.last, "AttemptId", "attemptId");
                                    const lastDone = r.last && pick(r.last, "Status", "status") === "Submitted";
                                    return (
                                        <tr key={r.empId}>
                                            <td>
                                                <div className="fw-semibold">{r.name || "—"}</div>
                                                <div className="small text-muted">{r.empId}{!r.active && <Badge color="light" className="text-muted ms-1">inactive</Badge>}</div>
                                            </td>
                                            <td>{r.department || "—"}</td>
                                            <td>{r.role || "—"}</td>
                                            <td>
                                                {!r.last ? <span className="text-muted">None</span> : lastDone && !canOpenResults ? (
                                                    <span>{fmtPercent(pick(r.last, "ScorePercent", "scorePercent"))} <BandBadge band={pick(r.last, "Band", "band")} /></span>
                                                ) : lastDone ? (
                                                    <Button size="sm" color="link" className="p-0 text-start" onClick={() => navigate(`/competency-quiz/result/${lastId}`)}>
                                                        {fmtPercent(pick(r.last, "ScorePercent", "scorePercent"))} <BandBadge band={pick(r.last, "Band", "band")} />
                                                        <div className="small text-muted">{fmtDateTime(pick(r.last, "SubmittedAt", "submittedAt"))}</div>
                                                    </Button>
                                                ) : <span className="text-warning">In progress</span>}
                                            </td>
                                            <td className="text-end">
                                                {r.isItAdmin ? (
                                                    <Badge color="info">IT Admin · always</Badge>
                                                ) : self ? (
                                                    <span className="small text-muted" title="Nobody can change their own access">You</span>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        color={r.assigned ? "light" : "primary"}
                                                        disabled={busy === r.empId || (!r.assigned && !r.active)}
                                                        onClick={() => toggle(r)}
                                                        title={!r.active && !r.assigned ? "Inactive employees cannot be assigned" : undefined}
                                                    >
                                                        {busy === r.empId ? <Spinner size="sm" /> : r.assigned ? "Unassign" : "Assign"}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                    {visible.length > SHOW_LIMIT && (
                        <div className="small text-muted mt-2">Showing the first {SHOW_LIMIT} of {visible.length}. Narrow the search to see more.</div>
                    )}
                </>
            )}
        </>
    );
}
